import React, { useEffect, useState } from "react";
import { X, DownloadSimple, DeviceMobile } from "@phosphor-icons/react";

/**
 * Lightweight "Add to Home Screen" hint.
 * - On Android/desktop Chrome/Edge: catches `beforeinstallprompt`, shows a button that triggers the native prompt.
 * - On iOS Safari (which doesn't support the API): shows manual instructions ("Share → Add to Home Screen").
 * Dismissable; remembers dismissal in localStorage.
 */
const KEY = "dftb26_install_dismissed";

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(KEY)) return;
    if (window.matchMedia("(display-mode: standalone)").matches) return; // already installed

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS detection: no beforeinstallprompt support, but we can suggest the manual flow
    const ua = window.navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const inSafari = /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
    if (isIos && inSafari) {
      const t = setTimeout(() => { setIosHint(true); setShow(true); }, 6000);
      return () => { window.removeEventListener("beforeinstallprompt", onPrompt); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => { localStorage.setItem(KEY, "1"); setShow(false); };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") localStorage.setItem(KEY, "1");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-prompt"
      role="dialog"
      aria-label="Install DFTB26 on your home screen"
      className="fixed left-1/2 -translate-x-1/2 bottom-24 z-50 w-[calc(100%-1.5rem)] max-w-md bg-white border border-[#E5E9F0] rounded-sm shadow-lg p-4 flex items-start gap-3 animate-[fadeIn_0.3s_ease-out]"
      style={{ boxShadow: "0 8px 32px rgba(59,29,130,0.18)" }}
    >
      <div className="w-10 h-10 rounded-sm bg-[#000000] grid place-items-center flex-shrink-0">
        <DeviceMobile size={20} weight="duotone" className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        {iosHint ? (
          <>
            <div className="font-display text-sm text-[#1c1c24]">Install DFTB26 on your iPhone</div>
            <p className="text-xs text-[#5a5a5a] mt-1">
              Tap <span className="font-semibold">Share</span> in Safari, then <span className="font-semibold">Add to Home Screen</span> - DFTB26 opens like an app, even offline.
            </p>
          </>
        ) : (
          <>
            <div className="font-display text-sm text-[#1c1c24]">Install DFTB26 on your phone</div>
            <p className="text-xs text-[#5a5a5a] mt-1">
              Add to home screen for one-tap access, faster loading, and offline programme.
            </p>
            <button
              onClick={install}
              data-testid="install-prompt-btn"
              className="mt-2 inline-flex items-center gap-1.5 h-9 px-3 rounded-sm bg-[#000000] text-white text-sm font-medium hover:bg-[#1c1c24] transition-colors"
            >
              <DownloadSimple size={14} /> Install
            </button>
          </>
        )}
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="install-prompt-dismiss"
        className="w-8 h-8 grid place-items-center rounded-sm hover:bg-[#F0F2F7] flex-shrink-0 -mr-1 -mt-1"
      >
        <X size={16} className="text-[#7A828C]" />
      </button>
    </div>
  );
}
